/**
 * Phone-number formatting helpers used across the frontend.
 * Supports US/Canada 10-digit numbers in the (NNN) NNN-NNNN format, and
 * international numbers that begin with "+": the leading "+" is preserved on
 * both display and save; only characters outside [+digits spaces ()-.] are
 * stripped. See SDA-112.
 */

/**
 * Live-format a phone string as the user types.
 *
 * - If the input starts with "+": international branch — strip disallowed
 *   characters (keep +, digits, spaces, parentheses, hyphens, dots), cap at
 *   25 characters, and return as-is with no US grouping.
 * - Otherwise: US branch — strips non-digits, caps at 10, inserts the
 *   standard (NNN) NNN-NNNN grouping progressively. Safe to call on every
 *   keystroke.
 */
export function formatPhoneDisplay(input: string): string {
  if (input.trimStart().startsWith('+')) {
    // International: trim leading whitespace, keep only allowed chars, cap at 25
    return input.trimStart().replace(/[^+\d ().-]/g, '').slice(0, 25);
  }

  // US/Canada: digits only, cap at 10, progressive grouping
  const digits = input.replace(/\D/g, '').slice(0, 10);
  if (digits.length > 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  } else if (digits.length > 3) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else if (digits.length > 0) {
    return `(${digits}`;
  }
  return '';
}

/**
 * Normalize a phone value before persisting to the database.
 *
 * - Starts with "+": international — preserve the leading "+" and keep
 *   digits, spaces, parentheses, hyphens, and dots (same allowed set as
 *   formatPhoneDisplay). This means the stored value matches what was
 *   displayed, so the field reloads without data loss.
 * - Otherwise: US branch — strip all non-digit characters (today's behavior).
 */
export function normalizePhoneForStore(input: string): string {
  const trimmed = input.trim();
  if (trimmed.startsWith('+')) {
    // Keep "+", digits, spaces, and common punctuation used in intl numbers.
    return trimmed.replace(/[^+\d ().-]/g, '');
  }
  // US/Canada: digits only
  return trimmed.replace(/\D/g, '');
}
